import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardActionArea,
  Badge,
  Avatar,
  Divider,
  IconButton,
  CircularProgress,
  Chip,
} from "@mui/material"
import {
  CloudOff,
  CloudDone,
  PeopleAlt,
  SwapHoriz,
  ReportProblem,
  Refresh,
  Dashboard as DashboardIcon,
  LocationOn,
  Shield,
  Public,
} from "@mui/icons-material"
import { PieChart } from "@mui/x-charts/PieChart"
import dayjs from "dayjs"
import localforage from "localforage"

import Header from "../../../../layouts/Header"
import { api } from "../../../../api"

// Tema SIAC
const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#ed6c02" },
    success: { main: "#2E7D32" },
    error: { main: "#d32f2f" },
  },
})

const DashboardCorRecin = () => {
  const navigate = useNavigate()
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [isLoading, setIsLoading] = useState(false)
  const [ultimaActualizacion, setUltimaActualizacion] = useState(dayjs().format("HH:mm:ss"))

  // ========================================================
  // ESTADOS LOGÍSTICOS
  // ========================================================
  const [recintoInfo, setRecintoInfo] = useState({ nombre: "Cargando métricas...", zona: "", esGlobal: false })
  const [statsActas, setStatsActas] = useState({ recibidas: 0, faltantes: 1 })
  const [incidenciasActivas, setIncidenciasActivas] = useState(0)
  const [delegadosFaltantes, setDelegadosFaltantes] = useState(0)

  // ========================================================
  // INICIALIZACIÓN Y POLLING
  // ========================================================
  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    cargarEstadisticasRecinto()

    const interval = setInterval(() => {
      if (navigator.onLine) cargarEstadisticasRecinto(true)
    }, 180000)

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
      clearInterval(interval)
    }
  }, [])

  const cargarEstadisticasRecinto = async (silencioso = false) => {
    if (!silencioso) setIsLoading(true)

    try {
      if (isOffline) throw new Error("Offline")

      const res = await api.post("/ControlElectoral/getStatsCoordinador", {})
      const dataReal = res.data.data || {}

      const recibidas = dataReal.mesas_recibidas || 0
      const totalMesas = dataReal.mesas_total || 0
      const faltantes = totalMesas > 0 ? totalMesas - recibidas : 1
      const esGlobal = dataReal.es_global || false

      setRecintoInfo({
        nombre: dataReal.recinto_nombre || "Desconocido",
        zona: esGlobal ? "Centro de Mando General" : "Asignación Táctica",
        esGlobal,
      })
      setStatsActas({ recibidas, faltantes })
      setIncidenciasActivas(dataReal.incidencias_activas || 0)
      setDelegadosFaltantes(dataReal.delegados_ausentes || 0)

      setUltimaActualizacion(dayjs().format("HH:mm:ss"))
      await localforage.setItem("coord_dashboard_cache", dataReal)
    } catch (error) {
      console.error("Error cargando estadísticas", error)
      const cache = await localforage.getItem("coord_dashboard_cache")
      if (cache) {
        const recibidas = cache.mesas_recibidas || 0
        const totalMesas = cache.mesas_total || 0
        const esGlobal = cache.es_global || false

        setRecintoInfo({
          nombre: cache.recinto_nombre || "Desconocido",
          zona: "Caché Offline",
          esGlobal,
        })
        setStatsActas({ recibidas, faltantes: totalMesas > 0 ? totalMesas - recibidas : 1 })
        setIncidenciasActivas(cache.incidencias_activas || 0)
        setDelegadosFaltantes(cache.delegados_ausentes || 0)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const nav = {
    asistencia: "/home/dashboard/ControlElectoral/Cordinador/ControlAsis",
    reasignacion: "/home/dashboard/ControlElectoral/Cordinador/ReasigMesas",
    incidencias: "/home/dashboard/ControlElectoral/Cordinador/AlertasSeg",
  }

  const chartData = [
    { id: 0, value: statsActas.recibidas, label: "Recibidas", color: "#2E7D32" },
    { id: 1, value: statsActas.faltantes, label: "Pendientes", color: "#e0e0e0" },
  ]

  const totalMesasReal =
    statsActas.recibidas + (statsActas.faltantes === 1 && statsActas.recibidas === 0 ? 0 : statsActas.faltantes)
  const porcentajeAvance = totalMesasReal > 0 ? Math.round((statsActas.recibidas / totalMesasReal) * 100) : 0

  return (
    <ThemeProvider theme={theme}>
      <Header />

      <div className="main main-app p-3 p-lg-4" style={{ backgroundColor: "#f5f7fa", minHeight: "100vh" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          p={2}
          bgcolor="white"
          borderRadius={2}
          boxShadow={1}
        >
          <Box display="flex" alignItems="center">
            {isOffline ? <CloudOff color="error" sx={{ mr: 1 }} /> : <CloudDone color="success" sx={{ mr: 1 }} />}
            <Typography variant="body2" fontWeight="bold" color={isOffline ? "error" : "success.main"}>
              {isOffline ? "OFFLINE (Revise su conexión)" : "EN LÍNEA"}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            {isLoading && <CircularProgress size={16} sx={{ mr: 1.5, color: "#196C87" }} />}
            <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>
              Actualizado: <b>{ultimaActualizacion}</b>
            </Typography>
            <IconButton
              onClick={() => cargarEstadisticasRecinto(false)}
              disabled={isLoading || isOffline}
              size="small"
              sx={{ border: "1px solid #196C87" }}
            >
              <Refresh fontSize="small" color="primary" />
            </IconButton>
          </Box>
        </Box>

        {/* PERFIL (DINÁMICO: ADMIN O COORDINADOR) */}
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            background: recintoInfo.esGlobal
              ? "linear-gradient(135deg, #0d3b4a 0%, #051c24 100%)"
              : "linear-gradient(135deg, #114b5e 0%, #0a2e3b 100%)",
            color: "white",
          }}
        >
          <Box display="flex" alignItems="center" mb={2}>
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 60, height: 60, mr: 2 }}>
              {recintoInfo.esGlobal ? <Public fontSize="large" /> : <Shield fontSize="large" />}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {recintoInfo.esGlobal ? "Administrador Electoral" : "Mando Logístico"}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {recintoInfo.esGlobal ? "Monitoreo General de Provincia" : "Control Táctico en Territorio"}
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ bgcolor: "rgba(255,255,255,0.2)", mb: 2 }} />
          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <Box display="flex" alignItems="flex-start">
              <LocationOn sx={{ mr: 1, mt: 0.5, color: recintoInfo.esGlobal ? "#4fc3f7" : "#ed6c02" }} />
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.8, textTransform: "uppercase", letterSpacing: 1 }}>
                  Cobertura
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {recintoInfo.nombre}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {recintoInfo.zona}
                </Typography>
              </Box>
            </Box>
            {recintoInfo.esGlobal && <Chip label="MODO GLOBAL" color="info" sx={{ fontWeight: "bold", mt: 1 }} />}
          </Box>
        </Paper>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box display="flex" alignItems="center" mb={1} width="100%">
                <DashboardIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="primary" fontWeight="bold">
                  Avance de Escrutinio
                </Typography>
              </Box>
              <Divider sx={{ width: "100%", mb: 2 }} />

              <Box position="relative" display="inline-flex" justifyContent="center" alignItems="center">
                <PieChart
                  series={[
                    {
                      data: chartData,
                      innerRadius: 60,
                      outerRadius: 100,
                      paddingAngle: 2,
                      cornerRadius: 5,
                    },
                  ]}
                  width={250}
                  height={250}
                  slotProps={{ legend: { hidden: true } }}
                  margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                />
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  bottom={0}
                  right={0}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {porcentajeAvance}%
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" justifyContent="space-around" width="100%" mt={2}>
                <Box textAlign="center">
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    {statsActas.recibidas}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" fontWeight="bold">
                    RECIBIDAS
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h5" fontWeight="bold" color="textSecondary">
                    {totalMesasReal}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" fontWeight="bold">
                    TOTAL MESAS
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Badge badgeContent={delegadosFaltantes} color="warning" sx={{ width: "100%" }}>
                  <Card elevation={2} sx={{ borderRadius: 3, width: "100%", borderLeft: "6px solid #196C87" }}>
                    <CardActionArea onClick={() => navigate(nav.asistencia)} sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ bgcolor: "#e3f2fd", color: "#196C87", mr: 2 }}>
                          <PeopleAlt />
                        </Avatar>
                        <Box flexGrow={1}>
                          <Typography variant="h6" fontWeight="bold">
                            Control de Asistencia
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Check-in y listado de delegados
                          </Typography>
                        </Box>
                        {delegadosFaltantes > 0 && (
                          <Typography variant="caption" fontWeight="bold" color="warning.main">
                            {delegadosFaltantes} ausentes
                          </Typography>
                        )}
                      </Box>
                    </CardActionArea>
                  </Card>
                </Badge>
              </Grid>

              <Grid item xs={12}>
                <Badge badgeContent={incidenciasActivas} color="error" sx={{ width: "100%" }}>
                  <Card elevation={2} sx={{ borderRadius: 3, width: "100%", borderLeft: "6px solid #d32f2f" }}>
                    <CardActionArea onClick={() => navigate(nav.incidencias)} sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ bgcolor: "#ffebee", color: "#d32f2f", mr: 2 }}>
                          <ReportProblem />
                        </Avatar>
                        <Box flexGrow={1}>
                          <Typography variant="h6" fontWeight="bold" color="error.main">
                            Alertas de Seguridad
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Atender llamadas de emergencia
                          </Typography>
                        </Box>
                      </Box>
                    </CardActionArea>
                  </Card>
                </Badge>
              </Grid>

              <Grid item xs={12}>
                <Card elevation={2} sx={{ borderRadius: 3, borderLeft: "6px solid #ed6c02" }}>
                  <CardActionArea onClick={() => navigate(nav.reasignacion)} sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center">
                      <Avatar sx={{ bgcolor: "#fff3e0", color: "#ed6c02", mr: 2 }}>
                        <SwapHoriz />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Reasignar Mesas
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Cubrir huecos de delegados ausentes
                        </Typography>
                      </Box>
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </ThemeProvider>
  )
}

export default DashboardCorRecin
